import { useMemo } from 'react'

export interface PaginationData {
  currentPage: number
  pageSize: number
  totalItems: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  pageNext?: string | null
  pagePrevious?: string | null
}

export interface PaginationOptions {
  maxPagesToShow?: number
  showEllipsis?: boolean
}

export interface PaginationResult {
  pages: (number | 'ellipsis')[]
  hasNextPage: boolean
  hasPreviousPage: boolean
  totalPages: number
  currentPage: number
  showingFrom: number
  showingTo: number
  totalItems: number
  pageNext?: string | null
  pagePrevious?: string | null
}

export function usePagination(
  data: PaginationData,
  options: PaginationOptions = {}
): PaginationResult {
  const { maxPagesToShow = 7, showEllipsis = true } = options

  const result = useMemo((): PaginationResult => {
    const { currentPage, totalItems, pageSize, pageNext, pagePrevious } = data
    const totalPages = Math.ceil(totalItems / pageSize)
    
    // Calculate showing range
    const showingFrom = totalItems > 0 ? (currentPage - 1) * pageSize + 1 : 0
    const showingTo = Math.min(currentPage * pageSize, totalItems)
    
    // Generate page numbers with ellipsis logic
    const pages: (number | 'ellipsis')[] = []
    
    if (totalPages <= maxPagesToShow || !showEllipsis) {
      // Show all pages if total pages is small or ellipsis is disabled
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      // Show pages with ellipsis logic
      const leftSiblingIndex = Math.max(currentPage - 1, 1)
      const rightSiblingIndex = Math.min(currentPage + 1, totalPages)
      
      const shouldShowLeftEllipsis = leftSiblingIndex > 2
      const shouldShowRightEllipsis = rightSiblingIndex < totalPages - 2
      
      const firstPageIndex = 1
      const lastPageIndex = totalPages
      
      if (!shouldShowLeftEllipsis && shouldShowRightEllipsis) {
        // Show: 1 2 3 4 5 ... 20
        const leftItemCount = 3 + 2 * 1 // 3 + 2 * left siblings
        const leftRange = Array.from({ length: leftItemCount }, (_, i) => i + 1)
        pages.push(...leftRange, 'ellipsis', lastPageIndex)
      } else if (shouldShowLeftEllipsis && !shouldShowRightEllipsis) {
        // Show: 1 ... 16 17 18 19 20
        const rightItemCount = 3 + 2 * 1 // 3 + 2 * right siblings
        const rightRange = Array.from(
          { length: rightItemCount },
          (_, i) => totalPages - rightItemCount + i + 1
        )
        pages.push(firstPageIndex, 'ellipsis', ...rightRange)
      } else if (shouldShowLeftEllipsis && shouldShowRightEllipsis) {
        // Show: 1 ... 8 9 10 ... 20
        const middleRange = Array.from(
          { length: rightSiblingIndex - leftSiblingIndex + 1 },
          (_, i) => leftSiblingIndex + i
        )
        pages.push(firstPageIndex, 'ellipsis', ...middleRange, 'ellipsis', lastPageIndex)
      } else {
        // Should not happen, but fallback to show all
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i)
        }
      }
    }

    return {
      pages,
      hasNextPage: !!pageNext,
      hasPreviousPage: !!pagePrevious,
      totalPages,
      currentPage,
      showingFrom,
      showingTo,
      totalItems,
      pageNext,
      pagePrevious,
    }
  }, [data, maxPagesToShow, showEllipsis])

  return result
}

export default usePagination